import { jsx } from "@builder.io/qwik/jsx-runtime";
import { createContextId, component$, useContext, useComputed$, useContextProvider } from "@builder.io/qwik";
const DEFAULT_RESOLUTIONS = [
  3840,
  1920,
  1280,
  960,
  640
];
const ImageContext = createContextId("ImageContext");
const useImageProvider = (state) => {
  useContextProvider(ImageContext, state);
};
const isValid = (value) => value || value === 0;
const getStyles = ({ placeholder, width, height, aspectRatio, objectFit = "cover", layout }) => {
  const isRatioValid = isValid(aspectRatio);
  if (height === "auto" && width === "auto" && isRatioValid) {
    console.warn(`To use the aspect ratio either set the width or the height`);
  }
  if (height !== "auto" && layout !== "fixed" && isRatioValid) {
    console.warn(`To maintain the aspect ratio we set 'height: "auto"'`);
  }
  const baseStyles = {
    "object-fit": objectFit,
    background: placeholder || "transparent"
  };
  switch (layout) {
    case "fixed":
      return {
        ...baseStyles,
        width: isValid(width) ? `${width}px` : void 0,
        height: isValid(height) ? `${height}px` : void 0
      };
    case "constrained":
      return {
        ...baseStyles,
        width: "100%",
        height: isRatioValid ? "auto" : void 0,
        "max-width": isValid(width) ? `${width}px` : void 0,
        "max-height": isValid(height) ? `${height}px` : void 0,
        "aspect-ratio": isRatioValid ? `${aspectRatio}` : void 0
      };
    case "fullWidth": {
      const heightStyle = {
        height: isRatioValid ? "auto" : isValid(height) ? `${height}px` : void 0
      };
      return {
        ...baseStyles,
        ...heightStyle,
        width: "100%",
        "aspect-ratio": isRatioValid ? `${aspectRatio}` : void 0
      };
    }
  }
};
const getSizes = ({ width, layout }) => {
  if (!width || !layout) {
    return void 0;
  }
  switch (layout) {
    case `constrained`:
      return `(min-width: ${width}px) ${width}px, 100vw`;
    case `fixed`:
      return `${width}px`;
    case `fullWidth`:
      return `100vw`;
    default:
      return void 0;
  }
};
const getSrcSet = async ({ src = "", width, height, aspectRatio, layout, resolutions, imageTransformer$ }) => {
  const breakpoints = getBreakpoints({
    width: typeof width === "string" ? parseInt(width, 10) : width,
    layout,
    resolutions: resolutions || DEFAULT_RESOLUTIONS
  });
  const srcSets = [];
  for await (const breakpoint of breakpoints.sort()) {
    let transformedHeight = typeof height === "string" ? parseInt(height, 10) : height;
    if (height && aspectRatio) {
      transformedHeight = Math.round(breakpoint / aspectRatio);
    }
    if (!imageTransformer$) {
      srcSets.push(`${src} ${breakpoint}w`);
      continue;
    }
    const transformed = await imageTransformer$({
      src,
      width: breakpoint,
      height: transformedHeight
    });
    srcSets.push(`${transformed} ${breakpoint}w`);
  }
  return srcSets.join(",\n");
};
const getBreakpoints = ({ width: widthAttribute, layout, resolutions = [] }) => {
  if (layout === "fullWidth") {
    return resolutions;
  }
  if (!widthAttribute) {
    return [];
  }
  const width = typeof widthAttribute === "string" ? parseInt(widthAttribute, 10) : widthAttribute;
  const doubleWidth = width * 2;
  if (layout === "fixed") {
    return [
      width,
      doubleWidth
    ];
  }
  if (layout === "constrained") {
    return [
      width,
      doubleWidth,
      ...resolutions.filter((w) => w < doubleWidth)
    ];
  }
  return [];
};
const Image = component$((props) => {
  const state = useContext(ImageContext);
  const { resolutions, imageTransformer$, ...imageAttributes } = {
    ...state,
    ...props
  };
  const imageAttributesWithoutChildren = {
    ...imageAttributes,
    children: void 0
  };
  const style = useComputed$(() => ({
    ...props.style,
    ...getStyles(props)
  }));
  const sizes = useComputed$(() => getSizes(props));
  const srcSet = useComputed$(() => {
    const { src, width: width2, height: height2, aspectRatio, layout } = props;
    return getSrcSet({
      src,
      width: width2,
      height: height2,
      aspectRatio,
      layout,
      resolutions,
      imageTransformer$
    });
  });
  const width = useComputed$(() => [
    "fullWidth",
    "constrained"
  ].includes(props.layout) ? void 0 : props.width);
  const height = useComputed$(() => [
    "fullWidth",
    "constrained"
  ].includes(props.layout) ? void 0 : props.height);
  return /* @__PURE__ */ jsx("img", {
    decoding: "async",
    ...imageAttributesWithoutChildren,
    style: style.value,
    width: width.value,
    height: height.value,
    srcset: srcSet.value,
    sizes: sizes.value
  });
});
export {
  DEFAULT_RESOLUTIONS,
  Image,
  ImageContext,
  getBreakpoints,
  getSizes,
  getSrcSet,
  getStyles,
  isValid,
  useImageProvider
};
